import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import Tooltip from "@mui/material/Tooltip";
import usePermissions from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/permissions";
import SeverityBadge from "./SeverityBadge";
import { ANOMALY_THEME } from "./anomalyTheme";
import { getBaseline, getCompanyId, isElectricityMetric, isYearOverYearAnomaly, listAnomalies, normalizeListAnomaly, PURCHASED_ELECTRICITY_METRIC_KEY, setAnomalyAcknowledged } from "../../services/anomalyApi";

const infoCardStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "14px 18px",
  borderRadius: "10px",
  border: `1px solid ${ANOMALY_THEME.cardBorder}`,
  background: "#fff",
  height: "100%",
};

const infoLabelStyle = {
  fontSize: "11px",
  fontWeight: 500,
  color: ANOMALY_THEME.grayText,
  textTransform: "uppercase",
  letterSpacing: "0.35px",
  margin: 0,
};

const infoValueStyle = {
  fontSize: "1rem",
  fontWeight: 600,
  color: ANOMALY_THEME.darkText,
  margin: "2px 0 0",
  lineHeight: 1.3,
};

const infoSubStyle = {
  fontSize: "0.75rem",
  color: ANOMALY_THEME.grayText,
  margin: "2px 0 0",
};

const MONTH_NAMES = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function periodYear(period) {
  if (!period) return null;
  const m = String(period).match(/^(20\d{2})/);
  return m ? m[1] : null;
}

function formatPeriod(period) {
  if (!period) return "—";
  const m = String(period).match(/^(\d{4})-(\d{2})$/);
  if (!m) return period;
  const month = parseInt(m[2], 10);
  return `${MONTH_NAMES[month] || m[2]} ${m[1]}`;
}

function formatAnomalyType(row) {
  const t = row.anomalyType || row.type;
  if (t === "same_month_baseline") return "Same-Month";
  if (t === "period_over_period") return "Year-over-Year";
  return t ? String(t).replace(/_/g, " ") : "—";
}

function formatBaseline(row) {
  if (row.expectedRange) {
    const { lower, upper } = row.expectedRange;
    if (lower != null && upper != null) {
      return `${Number(lower).toFixed(0)}–${Number(upper).toFixed(0)}`;
    }
  }
  if (row.median != null) {
    return `Median ${Number(row.median).toFixed(0)}`;
  }
  return row.previousValue != null && row.previousValue !== 0 ? Number(row.previousValue).toLocaleString() : "—";
}

function formatCurrentValue(value) {
  if (value == null || value === "") return "—";
  return Number(value).toLocaleString();
}

function formatMessage(message) {
  if (!message) return "—";
  return String(message)
    .replace(/\bElectricity\b/g, "Purchased Electricity")
    .replace(/\belectricity\b/g, "Purchased Electricity");
}

function applyYearFilter(items, yearFilter) {
  if (!yearFilter) return items;
  return items.filter((a) => {
    const y = periodYear(a.period) || (a.detectedAt ? String(new Date(a.detectedAt).getFullYear()) : "");
    return y === yearFilter;
  });
}

const countBadgeStyle = (variant, active) => {
  const base = { background: "#e9e9e9" };
  if (variant === "open") {
    return { ...base, color: active ? "#cc2229" : "#f5979f" };
  }
  return { ...base, color: active ? "#407775" : "#ada9a9" };
};

const SiteAnomaliesPanel = ({ siteLocationId, esgMetric }) => {
  const { has: hasPermission } = usePermissions();
  const canManageConfig = hasPermission(PERMISSIONS.MANAGE_CONFIGURATION);
  const [allRows, setAllRows] = useState([]);
  const [activeTab, setActiveTab] = useState("open");
  const [yearFilter, setYearFilter] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [apiError, setApiError] = useState(null);

  const load = useCallback(async () => {
    if (!isElectricityMetric(esgMetric)) {
      setAllRows([]);
      setBaseline(null);
      return;
    }

    const customerId = getCompanyId();
    if (!customerId) {
      setApiError("Company id not set. Log in again so companyid is stored.");
      return;
    }

    setLoading(true);
    setApiError(null);
    try {
      const facility = siteLocationId || "";
      const [anomalyRows, baselineRes] = await Promise.all([
        listAnomalies(customerId, facility),
        getBaseline(customerId, PURCHASED_ELECTRICITY_METRIC_KEY, facility),
      ]);

      const filtered = anomalyRows
        .map(normalizeListAnomaly)
        .filter((a) => a && !isYearOverYearAnomaly(a) && (!facility || a.siteLocation === facility || a.facility === facility))
        .sort((a, b) => new Date(b.detectedAt) - new Date(a.detectedAt));

      setAllRows(filtered);
      const years = [...new Set(filtered.map((a) => periodYear(a.period) || (a.detectedAt ? String(new Date(a.detectedAt).getFullYear()) : null)).filter(Boolean))].sort((a, b) => b.localeCompare(a));
      setYearOptions(years.map((y) => ({ label: y, value: y })));

      const b = baselineRes?.baseline;
      if (b) {
        setBaseline({
          value: b.baselineValue ?? b.lastValue ?? b.BaselineValue,
          updated: b.lastUpdated ?? b.LastUpdated,
        });
      } else {
        setBaseline(null);
      }
    } catch (e) {
      console.error("SiteAnomaliesPanel:", e);
      setApiError(e.message || "Could not load anomalies. Restart Node (8081), ecoprismapi (7009), and EcoUtil (8003).");
      setAllRows([]);
      setYearOptions([]);
    } finally {
      setLoading(false);
    }
  }, [siteLocationId, esgMetric]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onUpdated = () => load();
    window.addEventListener("anomaly-data-updated", onUpdated);
    return () => window.removeEventListener("anomaly-data-updated", onUpdated);
  }, [load]);

  const openRows = useMemo(
    () =>
      applyYearFilter(
        allRows.filter((a) => !a.acknowledged),
        yearFilter,
      ),
    [allRows, yearFilter],
  );

  const acknowledgedRows = useMemo(
    () =>
      applyYearFilter(
        allRows.filter((a) => a.acknowledged),
        yearFilter,
      ),
    [allRows, yearFilter],
  );

  const tableRows = activeTab === "open" ? openRows : acknowledgedRows;

  const notifyUpdated = () => {
    window.dispatchEvent(new CustomEvent("anomaly-data-updated"));
  };

  const handleAcknowledge = async (item) => {
    setActingId(item.id);
    try {
      await setAnomalyAcknowledged(item.id, true);
      await load();
      notifyUpdated();
    } catch (e) {
      console.error(e);
      setApiError(e.message);
    } finally {
      setActingId(null);
    }
  };

  const handleReopen = async (item) => {
    setActingId(item.id);
    try {
      await setAnomalyAcknowledged(item.id, false);
      await load();
      notifyUpdated();
    } catch (e) {
      console.error(e);
      setApiError(e.message);
    } finally {
      setActingId(null);
    }
  };

  if (!isElectricityMetric(esgMetric)) {
    return <p className="text-muted">Anomaly detection applies to Purchase Electricity configurations only.</p>;
  }

  const severityBody = (row) => <SeverityBadge severity={row.severity} />;

  const openActionBody = (row) => (
    <Tooltip title={canManageConfig ? "Acknowledge — moves to Acknowledged tab" : "You don't have permission to acknowledge anomalies"}>
      <span>
        <img
          className="action-icon"
          src="assets/img/deactivate.png"
          alt="Acknowledge"
          style={{
            cursor: (!canManageConfig || actingId === row.id) ? "not-allowed" : "pointer",
            opacity: (!canManageConfig || actingId === row.id) ? 0.45 : 1,
          }}
          onClick={() => canManageConfig && actingId !== row.id && handleAcknowledge(row)}
        />
      </span>
    </Tooltip>
  );

  const ackActionBody = (row) => (
    <Tooltip title={canManageConfig ? "Reopen — return to Open tab" : "You don't have permission to modify anomalies"}>
      <span>
        <img
          className="action-icon"
          src="assets/img/reactivate.png"
          alt="Reopen"
          style={{
            cursor: (!canManageConfig || actingId === row.id) ? "not-allowed" : "pointer",
            opacity: (!canManageConfig || actingId === row.id) ? 0.45 : 1,
          }}
          onClick={() => canManageConfig && actingId !== row.id && handleReopen(row)}
        />
      </span>
    </Tooltip>
  );

  const emptyOpen = "No open anomalies. Upload a report from All Configurations or acknowledge items below.";
  const emptyAck = "No acknowledged anomalies yet. Use the acknowledge icon on the Open tab.";

  const metricLabel = (esgMetric || "").toLowerCase().includes("electric") ? "Purchase Electricity" : esgMetric || "—";

  return (
    <div>
      <div className="row g-3 mb-3">
        <div className="col-12 col-md-4">
          <div style={infoCardStyle}>
            <PlaceOutlinedIcon sx={{ fontSize: 28, color: ANOMALY_THEME.primaryTeal }} />
            <div>
              <p style={infoLabelStyle}>Site Location</p>
              <p style={infoValueStyle}>{siteLocationId || "—"}</p>
              <p style={infoSubStyle}>{metricLabel}</p>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div style={infoCardStyle}>
            <ShowChartOutlinedIcon sx={{ fontSize: 28, color: ANOMALY_THEME.lightTeal }} />
            <div>
              <p style={infoLabelStyle}>Baseline (kWh)</p>
              <p style={{ ...infoValueStyle, color: ANOMALY_THEME.primaryTeal }}>{baseline?.value != null ? Number(baseline.value).toLocaleString() : "—"}</p>
              {baseline?.updated && <p style={infoSubStyle}>Updated {new Date(baseline.updated).toLocaleString()}</p>}
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div style={infoCardStyle}>
            <AssessmentOutlinedIcon sx={{ fontSize: 28, color: ANOMALY_THEME.warmGold }} />
            <div>
              <p style={infoLabelStyle}>Status</p>
              <p style={infoValueStyle}>
                <span style={{ color: ANOMALY_THEME.mutedRed }}>{openRows.length} open</span>
                <span style={{ color: ANOMALY_THEME.grayText, fontWeight: 400 }}> · </span>
                <span style={{ color: ANOMALY_THEME.primaryTeal }}>{acknowledgedRows.length} acknowledged</span>
              </p>
              <p style={infoSubStyle}>{yearFilter ? `Year ${yearFilter}` : "All years"}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div className="d-flex gap-3">
          <button type="button" className={activeTab === "open" ? "active-buttonred" : "inactive-buttonred"} onClick={() => setActiveTab("open")}>
            <span className="record-count" style={countBadgeStyle("open", activeTab === "open")}>
              {openRows.length}
            </span>
            <span className="record-title">Open</span>
          </button>
          <button type="button" className={activeTab === "acknowledged" ? "active-buttongreen" : "inactive-buttongreen"} onClick={() => setActiveTab("acknowledged")}>
            <span className="record-count" style={countBadgeStyle("acknowledged", activeTab === "acknowledged")}>
              {acknowledgedRows.length}
            </span>
            <span className="record-title">Acknowledged</span>
          </button>
        </div>
        <Dropdown value={yearFilter} options={[{ label: "All years", value: null }, ...yearOptions]} onChange={(e) => setYearFilter(e.value)} placeholder={yearOptions.length ? "Year" : "No years"} className="p-inputtext-sm" style={{ minWidth: "8rem", marginBottom: "4px" }} />
      </div>

      {apiError && (
        <p className="text-danger mb-2" style={{ fontSize: "0.85rem" }}>
          {apiError}
        </p>
      )}

      <DataTable value={tableRows} loading={loading} emptyMessage={activeTab === "open" ? emptyOpen : emptyAck} paginator={tableRows.length > 10} rows={10} rowsPerPageOptions={[10, 20, 50]} size="small" stripedRows scrollable tableStyle={{ minWidth: "80rem" }}>
        <Column header={activeTab === "open" ? "Acknowledge" : "Reopen"} body={activeTab === "open" ? openActionBody : ackActionBody} style={{ width: "7rem", textAlign: "center" }} />
        <Column field="period" header="Period" body={(r) => formatPeriod(r.period)} style={{ minWidth: "8rem", width: "8rem" }} />
        <Column field="type" header="Type" body={(r) => formatAnomalyType(r)} style={{ minWidth: "9rem", width: "9rem" }} />
        <Column field="severity" header="Severity" body={severityBody} style={{ width: "8.5rem" }} />
        <Column field="currentValue" header="Current (kWh)" body={(r) => formatCurrentValue(r.currentValue)} style={{ minWidth: "5rem", width: "5rem" }} />
        <Column header="Baseline" body={(r) => formatBaseline(r)} style={{ minWidth: "5rem", width: "5rem" }} />
        <Column header="History" body={(r) => (r.historicalCount ? `${r.historicalCount} yrs` : "—")} style={{ width: "6.5rem" }} />
        <Column field="change" header="Change %" body={(r) => `${Number(r.change || 0).toFixed(1)}%`} style={{ minWidth: "4.5rem", width: "4.5rem" }} />
        <Column field="message" header="Message" body={(r) => formatMessage(r.message)} style={{ minWidth: "24rem" }} />
        <Column field="detectedAt" header="Detected" body={(r) => (r.detectedAt ? new Date(r.detectedAt).toLocaleString() : "—")} style={{ width: "12rem" }} />
      </DataTable>
    </div>
  );
};

export default SiteAnomaliesPanel;
